/* eslint-disable no-prototype-builtins */
import { type ClassValue, clsx } from "clsx";
import qs from "query-string";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const DestinationOptions: any[] = [
  {
    value: "0",
    name: "Paris",
  },
  {
    value: "1",
    name: "Aéroport Orly",
  },
  {
    value: "2",
    name: "Aéroport Charles de Gaulle",
  },
  {
    value: "3",
    name: "Gare du Nord, Gare de Est",
  },
  {
    value: "4",
    name: "Autre",
  },
  {
    value: "5",
    name: "Gare Montparnasse",
  },
  {
    value: "6",
    name: "Gare de Lyon",
  },
  {
    value: "7",
    name: "Versailles",
  },
  {
    value: "8",
    name: "Parc Asterix",
  },
  {
    value: "9",
    name: "Disney",
  },
  {
    value: "A",
    name: "Beauvais",
  },
];

export const DestinationDetailsParis: any[] = [
  {
    value: "75001",
    name: "1er arrondisssement",
  },
  {
    value: "75002",
    name: "2eme arrondisssement",
  },
  {
    value: "75003",
    name: "3eme arrondisssement",
  },
  {
    value: "75004",
    name: "4eme arrondisssement",
  },
  {
    value: "75005",
    name: "5eme arrondisssement",
  },
  {
    value: "75006",
    name: "6eme arrondisssement",
  },
  {
    value: "75007",
    name: "7eme arrondisssement",
  },
  {
    value: "75008",
    name: "8eme arrondisssement",
  },
  {
    value: "75009",
    name: "9eme arrondisssement",
  },
  {
    value: "75010",
    name: "10eme arrondisssement",
  },
  {
    value: "75011",
    name: "11eme arrondisssement",
  },
  {
    value: "75012",
    name: "12eme arrondisssement",
  },
  {
    value: "75013",
    name: "13eme arrondisssement",
  },
  {
    value: "75014",
    name: "14eme arrondisssement",
  },
  {
    value: "75015",
    name: "15eme arrondisssement",
  },
  {
    value: "75016",
    name: "16eme arrondisssement",
  },
  {
    value: "75017",
    name: "17eme arrondisssement",
  },
  {
    value: "75018",
    name: "18eme arrondisssement",
  },
  {
    value: "75019",
    name: "19eme arrondisssement",
  },
  {
    value: "75020",
    name: "20eme arrondisssement",
  },
];

export const DestinationDetailsBanlieue: any[] = [
  {
    value: "91000",
    name: "91 Essonne",
  },
  {
    value: "92000",
    name: "92 Hauts-de-Seine",
  },
  {
    value: "93000",
    name: "93 Seine-Saint-Denis",
  },
  {
    value: "94000",
    name: "94 Seine-Saint-Denis",
  },
  {
    value: "95000",
    name: "95 Val d'Oise",
  },
  {
    value: "77000",
    name: "77 Seine et Marne",
  },
  {
    value: "78000",
    name: "78 Yvelines",
  },
];

export const parseStringify = (value: any) => JSON.parse(JSON.stringify(value));

interface UrlQueryParams {
  params: string;
  key: string;
  value: string;
}

export function formUrlQuery({ params, key, value }: UrlQueryParams) {
  const currentUrl = qs.parse(params);

  currentUrl[key] = value;

  return qs.stringifyUrl(
    {
      url: window.location.pathname,
      query: currentUrl,
    },
    { skipNull: true }
  );
}

// Normalize a French phone number to the stored "33…" username form. Mirrors
// the backend `normalize_phone_username`: strip separators, drop a single
// leading 0, then prefix "33". Non-numeric input (e.g. an email) is returned
// unchanged, and already-international numbers are left as-is. The backend
// re-applies the same normalization, so this is a convenience that keeps the
// historical frontend behavior without being load-bearing.
export const normalizePhoneUsername = (identifier: string): string => {
  const cleaned = identifier.replace(/[ .-]/g, "");
  if (cleaned === "" || !/^\d+$/.test(cleaned)) return identifier;
  if (cleaned.startsWith("33")) return cleaned;
  if (cleaned.startsWith("0")) return "33" + cleaned.slice(1);
  return "33" + cleaned;
};

export const authFormSchema = () =>
  z.object({
    // Single identifier for the unified sign-in page: a phone number (hôtels/
    // restaurants) or an email (entreprise/chauffeur). The form detects which
    // one it is by the presence of "@".
    identifier: z.string().min(4, "Merci d'indiquer votre téléphone ou votre email"),
    password: z.string().min(4),
  });

export const forgottenFormSchema = () =>
  z
    .object({
      // "hotel" -> reset by phone (username); "email" -> reset by email
      // (chauffeur or compte entreprise). The radio decides which field applies.
      account_type: z.enum(["hotel", "email"]),
      username: z.string().optional(), // phone, hôtels/restaurants
      email: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.account_type === "hotel") {
        if (!data.username || data.username.trim().length < 6) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["username"],
            message: "Veuillez indiquer votre numéro de téléphone",
          });
        }
      } else if (!data.email || !z.string().email().safeParse(data.email).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: "Adresse email invalide",
        });
      }
    });

// Course.message_for_taxi / CourseRequest.message_for_taxi in the backend
export const MESSAGE_FOR_TAXI_MAX_LENGTH = 40;

export const tripFormSchema = () =>
  z.object({
    schedule: z
      .date()
      .nullable()
      .optional(),
    //
    name: z
      .string()
      .max(15, { message: "Ce champ doit faire au plus 15 caractères" }),
    message_for_taxi: z
      .string()
      .max(MESSAGE_FOR_TAXI_MAX_LENGTH, {
        message: `Le message doit faire au plus ${MESSAGE_FOR_TAXI_MAX_LENGTH} caractères`,
      }),
    // passenger, when ordering from another departure: his full name, up to
    // the 40 characters of CourseRequest.name in the backend
    telephone_number: z.string().optional(),
    passenger_name: z
      .string()
      .max(40, { message: "Le nom du passager doit faire au plus 40 caractères" })
      .optional(),
  });

export const refinePhone = (val:string) => {
    if (!val) return false;
    const new_val = val.replace(/[\s.\-_]/g, '');
    if (new_val.length != 10) return false;
    const start_val = new_val.slice(0, 2);
    if (!['06', '07'].includes(start_val)) return false;
    return true;
  }

// Accepts any valid French phone number (mobile or landline), mirroring the
// backend FR_PHONE_REGEX (^0[1-9]\d{8}$). Used by the hotel offer sign-up form,
// where establishments may register with a landline number.
export const refineFrenchPhone = (val:string) => {
    if (!val) return false;
    const new_val = val.replace(/[\s.\-_]/g, '');
    return /^0[1-9]\d{8}$/.test(new_val);
  }

// Accepts a French number in either of the two forms the backend's
// `format_french_telephone_with_or_no_extention_or_None` recognises: as dialled
// (0…) or already in its stored "33…" username form. Used by the seller
// "Nouvel établissement" form, whose serializer runs that helper.
//
// It differs from `refineFrenchPhone` on both counts on purpose: 08 is refused
// (the backend's regexes stop at 7 and 9), and only the separators that helper
// strips — space, dot, hyphen — are stripped, so a number this accepts is a
// number the endpoint accepts.
export const refineFrenchPhoneOrUsername = (val:string) => {
    if (!val) return false;
    const new_val = val.replace(/[ .-]/g, '');
    return /^0[1-79]\d{8}$/.test(new_val) || /^33[1-79]\d{8}$/.test(new_val);
  }

export const giveback2FormSchema = (
  transfer: boolean,
  amount: number,
  setError: (value: string | null) => void
) =>
  z
    .object({
      giveback_iban_token: z.string().optional(),
      giveback_iban_amount: z.coerce.number().optional(),
      giveback_ibanA_token: z.string().optional(),
      giveback_ibanA_amount: z.coerce.number().optional(),
      giveback_ibanB_token: z.string().optional(),
      giveback_ibanB_amount: z.coerce.number().optional(),
      giveback_ibanC_token: z.string().optional(),
      giveback_ibanC_amount: z.coerce.number().optional(),
      giveback_ibanD_token: z.string().optional(),
      giveback_ibanD_amount: z.coerce.number().optional(),
      giveback_ibanE_token: z.string().optional(),
      giveback_ibanE_amount: z.coerce.number().optional(),
      giveback_ibanF_token: z.string().optional(),
      giveback_ibanF_amount: z.coerce.number().optional(),
      giveback_ibanG_token: z.string().optional(),
      giveback_ibanG_amount: z.coerce.number().optional(),
      giveback_ibanH_token: z.string().optional(),
      giveback_ibanH_amount: z.coerce.number().optional(),
      giveback_ibanI_token: z.string().optional(),
      giveback_ibanI_amount: z.coerce.number().optional(),
      //
      amount_for_email1: z.coerce.number().optional(),
      amount_for_email2: z.coerce.number().optional(),
      amount_for_email3: z.coerce.number().optional(),
      amount_for_email4: z.coerce.number().optional(),
      amount_for_email5: z.coerce.number().optional(),
      amount_for_email6: z.coerce.number().optional(),
      amount_for_email7: z.coerce.number().optional(),
      amount_for_email8: z.coerce.number().optional(),
      amount_for_email9: z.coerce.number().optional(),
      amount_for_email10: z.coerce.number().optional(),
      //
      email1: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email2: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email3: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email4: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email5: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email6: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email7: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email8: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email9: z.string().email("Cet email n'est pas valide").nullable().optional(),
      email10: z.string().email("Cet email n'est pas valide").nullable().optional(),
      //
      full_name: z.string().min(2, "Veuillez saisir votre nom et prénom"),
      mobile_phone: z.string().refine(refinePhone, {
        message: "Votre numéro de portable n'est pas reconnu",
      }),
      validation_code: z.string().optional(),
    })
    .refine(
      (data) => {
        setError("");
        if (transfer) {
          let total = 0;
          total += data.giveback_iban_amount || 0;
          total += data.giveback_ibanA_amount || 0;
          total += data.giveback_ibanB_amount || 0;
          total += data.giveback_ibanC_amount || 0;
          total += data.giveback_ibanD_amount || 0;
          total += data.giveback_ibanE_amount || 0;
          total += data.giveback_ibanF_amount || 0;
          total += data.giveback_ibanG_amount || 0;
          total += data.giveback_ibanH_amount || 0;
          total += data.giveback_ibanI_amount || 0;
          if (total < amount || total > amount) {
            return false;
          }
          return true;
        }
        // not transfer
        let total = 0;
        total += data.amount_for_email2 || 0;
        total += data.amount_for_email1 || 0;
        total += data.amount_for_email3 || 0;
        total += data.amount_for_email4 || 0;
        total += data.amount_for_email5 || 0;
        total += data.amount_for_email6 || 0;
        total += data.amount_for_email7 || 0;
        total += data.amount_for_email8 || 0;
        total += data.amount_for_email9 || 0;
        total += data.amount_for_email10 || 0;
        if (total < amount || total > amount) {
          return false;
        }
        return true;
      },
      () => {
        const message = "Le total doit être égal à " + amount + " euros";
        setError(message);
        return  { message };
      }
    );


    export const paymentFormSchema = (
      amount: number,
      setError: (value: string | null) => void
    ) =>
      z
        .object({
          giveback_iban_token: z.string().optional(),
          giveback_iban_amount: z.coerce.number().optional(),
          giveback_ibanA_token: z.string().optional(),
          giveback_ibanA_amount: z.coerce.number().optional(),
          giveback_ibanB_token: z.string().optional(),
          giveback_ibanB_amount: z.coerce.number().optional(),
          giveback_ibanC_token: z.string().optional(),
          giveback_ibanC_amount: z.coerce.number().optional(),
          giveback_ibanD_token: z.string().optional(),
          giveback_ibanD_amount: z.coerce.number().optional(),
          giveback_ibanE_token: z.string().optional(),
          giveback_ibanE_amount: z.coerce.number().optional(),
          giveback_ibanF_token: z.string().optional(),
          giveback_ibanF_amount: z.coerce.number().optional(),
          giveback_ibanG_token: z.string().optional(),
          giveback_ibanG_amount: z.coerce.number().optional(),
          giveback_ibanH_token: z.string().optional(),
          giveback_ibanH_amount: z.coerce.number().optional(),
          giveback_ibanI_token: z.string().optional(),
          giveback_ibanI_amount: z.coerce.number().optional(),
        })
        .refine(
          (data) => {
            setError("");
              let total = 0;
              total += data.giveback_iban_amount || 0;
              total += data.giveback_ibanA_amount || 0;
              total += data.giveback_ibanB_amount || 0;
              total += data.giveback_ibanC_amount || 0;
              total += data.giveback_ibanD_amount || 0;
              total += data.giveback_ibanE_amount || 0;
              total += data.giveback_ibanF_amount || 0;
              total += data.giveback_ibanG_amount || 0;
              total += data.giveback_ibanH_amount || 0;
              total += data.giveback_ibanI_amount || 0;
              console.log(total, amount, Math.abs(total - amount));
              if ( Math.abs(total - amount)  > 0.001) {
                return false;
              }
              return true;
          },
          () => {
            const message = "Le total doit être égal à " + amount + " euros";
            setError(message);
            return  { message };
          }
        );

export const cancellationFormSchema = () =>
  z.object({
    comment: z.string().max(200, { message: "Ce champ doit faire moins de 200 caractères" }).optional(),
    reason: z.string().optional(),
  });

export const complainFormSchema = () =>
  z.object({
    comment: z.string().max(200, { message: "Ce champ doit faire moins de 200 caractères" }),
  });

export const ibanpFormSchema = (
  iban_token: string,
  full_data_for_iban: boolean,
  beneficiary_professional: boolean = false
) => {
  // A professional iban only requires the name and the iban itself: nothing is
  // asked about the beneficiary, so nothing about them is validated either.
  const requires_beneficiary = !beneficiary_professional;
  const requires_full_data = full_data_for_iban && !beneficiary_professional;

  return z.object({
    name: z.string()
      .min(3, { message: "Ce nom est trop court" })
      .max(50, "Le nom ne peut pas dépasser 50 caractères"),
    beneficiary_firstname: requires_beneficiary ? z.string()
      .min(3, "Le prénom doit contenir au moins 3 caractères")
      .max(50, "Le prénom ne peut pas dépasser 50 caractères")
      : z.string().optional(),
    address: requires_full_data ? z.string()
      .min(5, "L'adresse doit contenir au moins 5 caractères")
      .max(100, "L'adresse ne peut pas dépasser 100 caractères")
      : z.string().optional(),
    beneficiary_zipcode: requires_full_data ? z.string()
      .regex(/^\d{5}$/, "Le code postal doit contenir exactement 5 chiffres")
      : z.string().optional(),

    beneficiary_birth_zipcode: requires_full_data ? z.string()
      .regex(/^\d{5}$/, "Le code postal de naissance doit contenir exactement 5 chiffres")
      : z.string().optional(),

    beneficiary_birth_city: requires_full_data ? z.string()
      .max(50, "Le nom de la ville ne peut pas dépasser 50 caractères")
      : z.string().optional(),
    beneficiary_city: requires_full_data ? z.string()
      .max(50, "Le nom de la ville ne peut pas dépasser 50 caractères")
      : z.string().optional(),
    beneficiary_birthday: requires_full_data ? z.date()
      .refine((date) => {
        const today = new Date();
        const eighteenYearsAgo = new Date(
          today.getFullYear() - 18,
          today.getMonth(),
          today.getDate()
        );
        return date <= eighteenYearsAgo;
      }, "Vous devez avoir au moins 18 ans")
      : z.date().nullable().optional(),

    beneficiary_security_number: requires_full_data ? z.string()
      .regex(/^[12][0-9]{2}(0[1-9]|1[0-2])[0-9]{2}[0-9]{3}[0-9]{3}[0-9]{2}$/,
        "Le numéro de sécurité sociale n'est pas valide")
      : z.string().optional(),

    iban1: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban2: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban3: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban4: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban5: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban6: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire 4 caractères" })
          .min(4, { message: "Ce champ doit faire 4 caractères" }),
    iban7: iban_token
      ? z.string().optional()
      : z
          .string()
          .max(4, { message: "Ce champ doit faire moins 4 caractères" }),
    beneficiary_professional: z.boolean().optional(),
  });
};

export type IbanFormValues = z.infer<ReturnType<typeof ibanpFormSchema>>;

